import React, { Component, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { ContactShadows, Environment, OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { PLATE, type CarState, type WindowId } from "../types";
import CarGraphic, { type Paint } from "./CarGraphic";
import { relayTick } from "../audio";

// 3D hero on a direct three.js scene so individual parts can move: door glass drops, boot lid
// hinges, lamps light, indicators flash on lock/unlock, body idles with the engine.
// Model: BHP3D "2022 Maruti Suzuki Swift Dzire" (CC-BY 4.0), Draco-compressed. Rig names:
//   door_{lf,rf,lr,rr}_dummy (hinge) › door_xx › door_xx_glass · boot_dummy (hinge) › boot
//   materials: primary (paint) · right_front_light (both headlamps) · left/right_rear_light ·
//   indicator_* · foglight_* · revlight_* · glass / glass.001 / glass.002
declare global { interface Window { __DZIRE_GLB?: string } }
const BASE = import.meta.env.BASE_URL;
const MODEL_URL = window.__DZIRE_GLB ?? `${BASE}models/dzire.glb`;
const DRACO_PATH = `${BASE}draco/`;
const ENV_URL = `${BASE}models/studio.jpg`;

const FORWARD = new THREE.Vector3(-0.02, -0.17, 1).normalize(); // driver's line of sight (nose is +Z)
const GLASS_DROP = 0.40;      // metres a door glass travels
const GLASS_SPEED = GLASS_DROP / 3.5; // ≈3.5 s full travel, like the real motor
const BOOT_OPEN = -1.05;      // rad
const DOOR_OPEN = 1.0;        // rad (~57°)
const DOOR_NODE: Record<WindowId, string> = { fl: "door_lf_dummy", fr: "door_rf_dummy", rl: "door_lr_dummy", rr: "door_rr_dummy" };
const DOOR_SIGN: Record<WindowId, number> = { fl: -1, fr: 1, rl: -1, rr: 1 }; // hinge at the front edge; swing outward
const GLASS_NODE: Record<WindowId, string> = { fl: "door_lf_glass", fr: "door_rf_glass", rl: "door_lr_glass", rr: "door_rr_glass" };

function hexToColor(hex: string) { return new THREE.Color(hex); }

// HSRP-style plate drawn to a canvas: white, black border, blue IND band, bold registration.
function makePlateTexture(text: string): THREE.CanvasTexture {
  const W = 1024, H = 256, c = document.createElement("canvas"); c.width = W; c.height = H;
  const g = c.getContext("2d")!;
  g.fillStyle = "#eef0f2"; g.fillRect(0, 0, W, H);
  g.lineWidth = 12; g.strokeStyle = "#14161a"; g.strokeRect(6, 6, W - 12, H - 12);
  g.fillStyle = "#153c96"; g.fillRect(12, 12, 104, H - 24);
  g.fillStyle = "#fff"; g.font = "700 38px Arial, Helvetica, sans-serif"; g.textAlign = "center"; g.textBaseline = "middle";
  g.fillText("IND", 64, H - 52);
  g.strokeStyle = "#fff"; g.lineWidth = 3; g.beginPath(); g.arc(64, 62, 17, 0, Math.PI * 2); g.stroke();
  g.fillStyle = "#121418"; g.font = "700 150px Arial, Helvetica, sans-serif";
  g.fillText(text, (W + 116) / 2, H / 2 + 6);
  const t = new THREE.CanvasTexture(c); t.flipY = true; /* plate UVs run bottom-up */ t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8;
  return t;
}
// ---- The real car's centre stack: 9" floating Android head unit + 3-knob manual A/C ----
function launcherTexture(): THREE.CanvasTexture {
  const W = 1024, H = 576, c = document.createElement("canvas"); c.width = W; c.height = H; const g = c.getContext("2d")!;
  g.fillStyle = "#0a0c11"; g.fillRect(0, 0, W, H);
  // status bar
  g.fillStyle = "#e8ecf2"; g.font = "600 30px Arial"; g.textBaseline = "middle";
  g.fillText("\u21B6", 40, 44); g.fillText("\u2302", 92, 44); g.fillText("\u25AD", 140, 44);
  const now = new Date(); const hh = now.getHours() % 12 || 12, mm = String(now.getMinutes()).padStart(2, "0"), ap = now.getHours() < 12 ? "AM" : "PM";
  g.fillStyle = "#5fd4ff"; g.font = "600 30px Arial"; g.textAlign = "right"; g.fillText(`${hh}:${mm}  ${ap}`, W - 34, 44); g.textAlign = "left";
  // four launcher tiles
  const tiles: [string, string][] = [["#3fcf86", "#1fa86a"], ["#c84ad6", "#7a3de0"], ["#38b8ea", "#2596d8"], ["#f4c728", "#e3a512"]];
  const tw = 214, th = 356, ty = 84, gap = 26, x0 = (W - (4 * tw + 3 * gap)) / 2;
  tiles.forEach(([a, b], i) => {
    const x = x0 + i * (tw + gap); const gr = g.createLinearGradient(x, ty, x, ty + th); gr.addColorStop(0, a); gr.addColorStop(1, b);
    g.fillStyle = gr; g.beginPath(); g.roundRect(x, ty, tw, th, 10); g.fill();
    g.strokeStyle = "rgba(255,255,255,0.85)"; g.lineWidth = 6; g.fillStyle = "rgba(255,255,255,0.92)";
    if (i === 0) { // clock
      g.beginPath(); g.arc(x + tw / 2, ty + 120, 66, 0, Math.PI * 2); g.stroke();
      g.lineWidth = 8; g.beginPath(); g.moveTo(x + tw / 2, ty + 120); g.lineTo(x + tw / 2 + 4, ty + 68); g.stroke();
      g.strokeStyle = "#d33"; g.lineWidth = 4; g.beginPath(); g.moveTo(x + tw / 2, ty + 120); g.lineTo(x + tw / 2 - 40, ty + 128); g.stroke();
      g.font = "300 64px Arial"; g.fillText(`${hh}:${mm}`, x + 28, ty + 258); g.font = "500 18px Arial"; g.fillText(ap, x + 170, ty + 236);
      g.font = "400 16px Arial"; g.fillText(now.toISOString().slice(0, 10), x + 60, ty + 304);
    } else if (i === 1) { // music
      g.fillStyle = "#111"; g.beginPath(); g.arc(x + tw / 2, ty + 110, 58, 0, Math.PI * 2); g.fill();
      g.fillStyle = "#e33d6b"; g.beginPath(); g.arc(x + tw / 2, ty + 110, 22, 0, Math.PI * 2); g.fill();
      g.fillStyle = "#fff"; g.font = "500 22px Arial"; g.fillText("Sleeping? (Brother Jo", x + 10, ty + 208);
      g.font = "400 40px Arial"; g.fillText("\u25C0\u25C0   \u25B6\u275A\u275A   \u25B6\u25B6", x + 20, ty + 300);
    } else if (i === 2) { // navi
      g.strokeStyle = "rgba(255,255,255,0.5)"; g.lineWidth = 3; g.setLineDash([14, 14]);
      g.beginPath(); g.moveTo(x + tw / 2 - 90, ty + 340); g.lineTo(x + tw / 2 - 30, ty + 40); g.moveTo(x + tw / 2 + 90, ty + 340); g.lineTo(x + tw / 2 + 30, ty + 40); g.stroke(); g.setLineDash([]);
      g.fillStyle = "#fff"; g.beginPath(); g.moveTo(x + tw / 2, ty + 140); g.lineTo(x + tw / 2 + 62, ty + 260); g.lineTo(x + tw / 2, ty + 225); g.lineTo(x + tw / 2 - 62, ty + 260); g.closePath(); g.fill();
    } else { // radio
      g.fillStyle = "#111"; g.beginPath(); g.arc(x + tw / 2, ty + 110, 58, 0, Math.PI * 2); g.fill();
      g.fillStyle = "#e8e8e8"; for (let k = 0; k < 13; k++) g.fillRect(x + tw / 2 - 42 + k * 7, ty + 92, 3, 36);
      g.fillStyle = "#f33"; g.fillRect(x + tw / 2 - 2, ty + 86, 4, 48);
      g.fillStyle = "#fff"; g.font = "400 40px Arial"; g.fillText("\u25C0\u25C0    \u25A3    \u25B6\u25B6", x + 20, ty + 300);
    }
  });
  // app row
  const apps: [string, string][] = [["Play Store", "#34a853"], ["Apps", "#f38a2b"], ["Navi", "#3b7bff"], ["Radio", "#2d6ef0"], ["Video", "#ff5a3c"], ["Music", "#f04c5b"]];
  apps.forEach(([n, col], i) => {
    const x = 74 + i * 160; g.fillStyle = col; g.beginPath(); g.roundRect(x, 462, 46, 46, 10); g.fill();
    g.fillStyle = "#fff"; g.font = "500 19px Arial"; g.textAlign = "center"; g.fillText(n, x + 23, 540); g.textAlign = "left";
  });
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; return t;
}

function climateTexture(ac: boolean): THREE.CanvasTexture {
  const W = 1024, H = 376, c = document.createElement("canvas"); c.width = W; c.height = H; const g = c.getContext("2d")!;
  g.fillStyle = "#15171b"; g.fillRect(0, 0, W, H);
  const rings = [170, 512, 854]; g.strokeStyle = "#3a3d44"; g.lineWidth = 10;
  rings.forEach((cx) => { g.beginPath(); g.arc(cx, 200, 118, 0, Math.PI * 2); g.stroke(); });
  g.strokeStyle = "#d8dbe0"; g.lineWidth = 6; g.lineCap = "round";
  // mode dial: arc + 5 glyph dots
  g.beginPath(); g.arc(170, 200, 140, -Math.PI * 0.85, Math.PI * 0.55); g.stroke();
  g.fillStyle = "#d8dbe0"; [-0.85, -0.5, -0.15, 0.2, 0.55].forEach((a) => { g.beginPath(); g.arc(170 + 160 * Math.cos(a * Math.PI), 200 + 160 * Math.sin(a * Math.PI), 7, 0, Math.PI * 2); g.fill(); });
  // fan dial: 0 1 2 3 4
  g.font = "600 44px Arial"; g.textAlign = "center"; g.textBaseline = "middle";
  ["0", "1", "2", "3", "4"].forEach((d, i) => { const a = (-150 + i * 30) * Math.PI / 180; g.fillText(d, 512 + 156 * Math.cos(a), 200 + 156 * Math.sin(a)); });
  g.font = "400 30px Arial"; g.fillText("\u2744", 512 - 150, 300);
  // temp dial: blue -> red arc
  const gr = g.createLinearGradient(700, 0, 1010, 0); gr.addColorStop(0, "#5fb0ff"); gr.addColorStop(0.5, "#d8dbe0"); gr.addColorStop(1, "#ff3a3a");
  g.strokeStyle = gr; g.lineWidth = 12; g.beginPath(); g.arc(854, 200, 142, -Math.PI * 0.85, Math.PI * 0.35); g.stroke();
  // A/C button + LED
  g.fillStyle = "#1e2126"; g.beginPath(); g.roundRect(640, 26, 120, 52, 8); g.fill();
  g.fillStyle = "#e8ecf2"; g.font = "700 30px Arial"; g.fillText("A/C", 682, 52);
  g.fillStyle = ac ? "#ff8a1f" : "#3a2418"; g.beginPath(); g.arc(742, 52, 9, 0, Math.PI * 2); g.fill();
  if (ac) { g.shadowColor = "#ff8a1f"; g.shadowBlur = 24; g.fill(); g.shadowBlur = 0; }
  // recirculation / fresh buttons
  [[300, 330], [700, 330]].forEach(([x, y]) => { g.fillStyle = "#1e2126"; g.beginPath(); g.roundRect(x, y - 22, 96, 44, 8); g.fill(); g.strokeStyle = "#cfd3d9"; g.lineWidth = 3; g.beginPath(); g.roundRect(x + 24, y - 10, 48, 20, 6); g.stroke(); });
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; return t;
}

function Dash({ s, anchor, level }: { s: CarState; anchor: THREE.Vector3; level: React.MutableRefObject<number> }) {
  const lit = s.engineRunning || s.ignition;
  const launcher = useMemo(() => launcherTexture(), []);
  const climate = useMemo(() => climateTexture(s.ac), [s.ac]);
  const dark = useRef<THREE.MeshStandardMaterial[]>([]);
  useFrame(() => { for (const m of dark.current) m.color.copy(m.userData.base as THREE.Color).multiplyScalar(level.current); });
  const reg = (m: THREE.MeshStandardMaterial | null) => { if (m && !dark.current.includes(m)) { m.userData.base = m.color.clone(); dark.current.push(m); } };
  const fanAngle = (-60 + s.blower * 30) * Math.PI / 180;
  const knob = (x: number, angle: number) => (
    <group position={[x, 0, 0.006]} rotation={[0, 0, -angle]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.023, 0.025, 0.014, 32]} /><meshStandardMaterial ref={reg} color="#2a2d33" roughness={0.5} metalness={0.35} /></mesh>
      <mesh position={[0, 0.013, 0.0075]}><boxGeometry args={[0.004, 0.02, 0.001]} /><meshStandardMaterial color="#e8ecf2" emissive="#e8ecf2" emissiveIntensity={lit ? 0.6 : 0} /></mesh>
    </group>
  );
  return (
    <group position={anchor}>
      {/* head unit: bezel + screen, faces the rear (-Z), slight upward tilt */}
      <group position={[0, 0.038, -0.02]} rotation={[-0.06, Math.PI, 0]}>
        <mesh position={[0, 0, -0.008]}><boxGeometry args={[0.236, 0.138, 0.016]} /><meshStandardMaterial ref={reg} color="#141619" roughness={0.45} metalness={0.1} /></mesh>
        <mesh position={[0, 0, 0.0005]}>
          <planeGeometry args={[0.216, 0.122]} />
          <meshStandardMaterial map={launcher} emissiveMap={launcher} emissive="#ffffff" emissiveIntensity={lit ? 1.5 : 0} color={lit ? "#ffffff" : "#05070a"} roughness={0.12} metalness={0} toneMapped={false} />
        </mesh>
      </group>
      {/* manual A/C panel with three knobs */}
      <group position={[0, -0.105, -0.008]} rotation={[0, Math.PI, 0]}>
        <mesh><planeGeometry args={[0.245, 0.09]} /><meshStandardMaterial map={climate} emissiveMap={climate} emissive="#ffffff" emissiveIntensity={lit ? 0.35 : 0.06} roughness={0.7} /></mesh>
        {knob(-0.084, 0.55)}
        {knob(0, fanAngle)}
        {knob(0.084, 0.9)}
      </group>
    </group>
  );
}

// Soft radial glow sprite for indicator bloom.
let glowTex: THREE.CanvasTexture | null = null;
function getGlowTexture(): THREE.CanvasTexture {
  if (glowTex) return glowTex;
  const c = document.createElement("canvas"); c.width = c.height = 128; const g = c.getContext("2d")!;
  const r = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  r.addColorStop(0, "rgba(255,190,80,1)"); r.addColorStop(0.35, "rgba(255,160,40,0.55)"); r.addColorStop(1, "rgba(255,140,20,0)");
  g.fillStyle = r; g.fillRect(0, 0, 128, 128);
  glowTex = new THREE.CanvasTexture(c); glowTex.colorSpace = THREE.SRGBColorSpace; return glowTex;
}
// The model's plates share material "grille.2" and use this sub-rectangle of its 1024² texture.
const PLATE_UV = { u0: 0.152, u1: 0.389, v0: 0.151, v1: 0.211 };

function CarModel({ s, paint }: { s: CarState; paint: Paint }) {
  const { camera, controls } = useThree() as { camera: THREE.PerspectiveCamera; controls: { target: THREE.Vector3; autoRotate: boolean; maxPolarAngle: number; minPolarAngle: number } | null };
  const { scene } = useGLTF(MODEL_URL, DRACO_PATH);
  const group = useRef<THREE.Group>(null);
  const cabin = useRef<THREE.PointLight>(null);
  const parked = useRef(false); const glide = useRef(false); const unglide = useRef(false); const cabinLevel = useRef(0.12);
  const lit = s.engineRunning || s.ignition;

  // One-time: orient (front → +Z), ground at y=0, centre; upgrade materials.
  const rig = useMemo(() => {
    // idempotent (StrictMode runs this twice): measure the raw scene, not a previous wrap
    scene.removeFromParent(); scene.position.set(0, 0, 0); scene.rotation.set(0, 0, 0); scene.scale.set(1, 1, 1);
    scene.updateMatrixWorld(true);
    const get = (n: string) => scene.getObjectByName(n) ?? null;
    // group pivots all sit at the origin in this rig, so use mesh bounds to find the nose
    const head = get("headlight_led"), tail = get("tailights_r");
    const front = new THREE.Vector3(0, 0, 1);
    if (head && tail) {
      const a = new THREE.Box3().setFromObject(head).getCenter(new THREE.Vector3());
      const b = new THREE.Box3().setFromObject(tail).getCenter(new THREE.Vector3());
      front.copy(a.sub(b)); front.y = 0; if (front.lengthSq() > 1e-6) front.normalize(); else front.set(0, 0, 1);
    }
    const yaw = Math.atan2(front.x, front.z); // rotate so the nose points +Z
    if (DEV_QS.has("debug")) console.log("[car] head", !!head, "tail", !!tail, "front", front.toArray().map((v) => v.toFixed(2)), "yaw", yaw.toFixed(2));
    const wrap = new THREE.Group(); wrap.rotation.y = -yaw; wrap.add(scene); wrap.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(wrap);
    const c = box.getCenter(new THREE.Vector3());
    wrap.position.set(-c.x, -box.min.y, -c.z);
    const size = box.getSize(new THREE.Vector3());

    const mats = new Map<string, THREE.MeshStandardMaterial>();
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = false; m.receiveShadow = false;
      const list = Array.isArray(m.material) ? m.material : [m.material];
      list.forEach((mat) => { if ((mat as THREE.MeshStandardMaterial).isMeshStandardMaterial) mats.set(mat.name, mat as THREE.MeshStandardMaterial); });
    });
    // paint → physical clearcoat
    const prim = mats.get("primary");
    let paintMat: THREE.MeshPhysicalMaterial | null = null;
    if (prim) {
      paintMat = new THREE.MeshPhysicalMaterial({ color: hexToColor(paint.base), metalness: 0.55, roughness: 0.32, clearcoat: 1, clearcoatRoughness: 0.05, envMapIntensity: 1.2 });
      paintMat.name = "primary";
      scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && (m.material as THREE.Material).name === "primary") m.material = paintMat!; });
    }
    // glass
    for (const n of ["glass", "glass.002"]) {
      const g = mats.get(n); if (!g) continue;
      g.transparent = true; g.opacity = 0.28; g.roughness = 0.05; g.metalness = 0; g.envMapIntensity = 1.6; g.depthWrite = false; g.color.set("#9fb4c8");
    }
    const red = mats.get("glass.001"); if (red) { red.transparent = true; red.opacity = 0.55; red.roughness = 0.1; red.depthWrite = false; }
    // number plates: remap the two plate meshes' UVs onto a crisp dedicated texture
    const plateMat = new THREE.MeshStandardMaterial({ map: makePlateTexture(PLATE), roughness: 0.5, metalness: 0 });
    scene.traverse((o) => {
      const m = o as THREE.Mesh; if (!m.isMesh || (m.material as THREE.Material).name !== "grille.2") return;
      m.geometry.computeBoundingBox(); const bb = m.geometry.boundingBox!; const w = bb.max.x - bb.min.x;
      if (w < 0.4 || w > 0.6) return; // the grille itself also uses this material
      const geo = m.geometry.clone(); const uv = geo.attributes.uv as THREE.BufferAttribute;
      const cl = (x: number) => Math.min(1, Math.max(0, x));
      for (let i = 0; i < uv.count; i++) uv.setXY(i, cl((uv.getX(i) - PLATE_UV.u0) / (PLATE_UV.u1 - PLATE_UV.u0)), cl((uv.getY(i) - PLATE_UV.v0) / (PLATE_UV.v1 - PLATE_UV.v0)));
      uv.needsUpdate = true; m.geometry = geo; m.material = plateMat;
    });
    // lamps: glow through their own textures
    const lamp = (n: string) => { const m = mats.get(n); if (m) { m.emissiveMap = m.map ?? null; m.emissive.set("#000"); } return m ?? null; };
    const lamps = {
      head: lamp("right_front_light"),
      tail: [lamp("left_rear_light"), lamp("right_rear_light")].filter(Boolean) as THREE.MeshStandardMaterial[],
      ind: ["indicator_lf", "indicator_rr", "indicator_lm", "indicator_rm", "indicator_lr"].map(lamp).filter(Boolean) as THREE.MeshStandardMaterial[],
      fog: [lamp("foglight_l"), lamp("foglight_r")].filter(Boolean) as THREE.MeshStandardMaterial[],
    };
    const glass: Partial<Record<WindowId, THREE.Object3D>> = {};
    (Object.keys(GLASS_NODE) as WindowId[]).forEach((w) => { const n = get(GLASS_NODE[w]); if (n) glass[w] = n; });
    // driver's eye point (RHD rig): above and behind the steering wheel, in centred world space
    wrap.updateMatrixWorld(true);
    const steer = get("movsteer_1.0") ?? get("steering_dummy");
    const sc = steer ? new THREE.Box3().setFromObject(steer).getCenter(new THREE.Vector3()) : new THREE.Vector3(0.34, 0.95, 0.4);
    const eye = new THREE.Vector3(sc.x, sc.y + 0.27, sc.z - 0.50);
    // interior stays dark until unlocked: meshes inside the cabin volume get their own material
    // copies with (almost) no environment light; the dome lamp + a raised env level come with unlock
    const cabinBox = new THREE.Box3(new THREE.Vector3(-0.9, 0.3, -1.5), new THREE.Vector3(0.9, 1.25, 0.95));
    const interiorMats: THREE.MeshStandardMaterial[] = []; const cloneOf = new Map<THREE.Material, THREE.MeshStandardMaterial>();
    const skip = new Set(["primary", "glass", "glass.001", "glass.002", "headlight_glass.1", "grille.2"]);
    scene.traverse((o) => {
      const m = o as THREE.Mesh; if (!m.isMesh) return;
      const mat = m.material as THREE.MeshStandardMaterial; if (!mat.isMeshStandardMaterial || skip.has(mat.name) || /indicator|light/.test(mat.name)) return;
      const c = new THREE.Box3().setFromObject(m).getCenter(new THREE.Vector3());
      if (!cabinBox.containsPoint(c)) return;
      let cl = cloneOf.get(mat);
      if (!cl) { cl = mat.clone(); cl.envMapIntensity = 0.05; cl.userData.base = cl.color.clone(); cl.color.multiplyScalar(0.12); cloneOf.set(mat, cl); interiorMats.push(cl); }
      m.material = cl;
    });
    // indicator lens positions (world, centred) for the glow sprites
    const indPos: THREE.Vector3[] = [];
    scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && /^indicator/.test((m.material as THREE.Material).name)) indPos.push(new THREE.Box3().setFromObject(m).getCenter(new THREE.Vector3())); });
    // dashboard overlay anchor: centre of the stock screen's driver-facing face (world, centred)
    const sg = get("screen_glass");
    const dashAnchor = sg ? new THREE.Box3().setFromObject(sg).getCenter(new THREE.Vector3()) : new THREE.Vector3(0, 0.95, 0.35);
    const boot = get("boot_dummy");
    const doors: Partial<Record<WindowId, THREE.Object3D>> = {};
    (Object.keys(DOOR_NODE) as WindowId[]).forEach((w) => { const n = get(DOOR_NODE[w]); if (n) doors[w] = n; });
    return { wrap, size, paintMat, lamps, glass, boot, doors, eye, centre: new THREE.Vector3(0, 0.72, 0), interiorMats, indPos, dashAnchor };
  }, [scene]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { rig.paintMat?.color.set(paint.base); }, [paint, rig]);

  // lamps follow ignition / engine
  useEffect(() => {
    const set = (m: THREE.MeshStandardMaterial | null, col: string, i: number) => { if (m) { m.emissive.set(col); m.emissiveIntensity = i; } };
    set(rig.lamps.head, lit ? "#ffffff" : "#000000", 2.2);
    rig.lamps.tail.forEach((m) => set(m, lit ? "#ff2a33" : "#000000", 1.8));
  }, [lit, rig]);

  // indicators flash on lock (1) / unlock (2), with the relay tick
  const flashes = useRef(0); const firstLock = useRef(true);
  const [ind, setInd] = useState(false);
  useEffect(() => {
    if (firstLock.current) { firstLock.current = false; return; }
    flashes.current = s.locked ? 1 : 2;
    let on = false; let n = 0;
    const id = setInterval(() => {
      on = !on; setInd(on); relayTick(on);
      if (!on && ++n >= flashes.current) { clearInterval(id); setInd(false); }
    }, 320);
    return () => clearInterval(id);
  }, [s.locked]);
  useEffect(() => {
    const on = ind || DEV_QS.has("flash");
    rig.lamps.ind.forEach((m) => { m.emissiveMap = null; m.emissive.set(on ? "#ffa310" : "#000"); m.emissiveIntensity = 6; m.needsUpdate = true; });
  }, [ind, rig]);

  // per-frame: glass travel at motor speed, boot hinge, idle shake
  useFrame((st, dt) => {
    (Object.keys(GLASS_NODE) as WindowId[]).forEach((w) => {
      const n = rig.glass[w]; if (!n) return;
      const target = -GLASS_DROP * (s.windows[w] / 100);
      const d = target - n.position.z; const step = Math.sign(d) * Math.min(Math.abs(d), GLASS_SPEED * dt);
      n.position.z += step;
    });
    if (rig.boot) { const t = s.bootOpen ? BOOT_OPEN : 0; rig.boot.rotation.x += (t - rig.boot.rotation.x) * Math.min(1, dt * 4); }
    (Object.keys(DOOR_NODE) as WindowId[]).forEach((w) => {
      const n = rig.doors[w]; if (!n) return;
      const t = s.doors[w] ? DOOR_OPEN * DOOR_SIGN[w] : 0;
      n.rotation.z += (t - n.rotation.z) * Math.min(1, dt * 3.5);
    });
    // cabin lamp: on while unlocked or any door is open
    if (cabin.current) {
      // camera inside the car's box → we're in the cabin: lamp on, no auto-rotate, free pitch
      const cp = camera.position;
      const dist = controls ? cp.distanceTo(controls.target) : 10;
      const near = dist < 2.6;
      // park at the driver's eyes: zoom in past 1.6 m and the camera glides into the seat looking
      // forward (pivot 15 cm ahead of the eye, so dragging = looking around); zoom back out past 1.2 m
      // to leave, and the pivot returns to the cabin centre
      if (controls) {
        if (!parked.current && dist < 1.6) { parked.current = true; glide.current = true; }
        else if (parked.current && dist > 1.2 && !glide.current) { parked.current = false; unglide.current = true; }
        if (glide.current) {
          const tgt = DEV_QS.has("stack") ? rig.dashAnchor.clone() : rig.eye.clone().add(FORWARD.clone().multiplyScalar(0.15)); // ?stack=1 (dev): look at the centre stack
          const k = Math.min(1, dt * 4);
          controls.target.lerp(tgt, k); cp.lerp(rig.eye, k);
          camera.fov += (60 - camera.fov) * k; camera.updateProjectionMatrix();
          if (cp.distanceTo(rig.eye) < 0.005 && Math.abs(camera.fov - 60) < 0.3) { glide.current = false; }
        } else if (unglide.current) {
          const d = rig.centre.clone().sub(controls.target); const step = d.multiplyScalar(Math.min(1, dt * 3));
          controls.target.add(step); cp.add(step);
          camera.fov += (26 - camera.fov) * Math.min(1, dt * 3); camera.updateProjectionMatrix();
          if (d.length() < 0.01 && Math.abs(camera.fov - 26) < 0.3) unglide.current = false;
        }
      }
      if (controls) { controls.autoRotate = !near && !DEV_QS.has("static"); controls.maxPolarAngle = near ? 2.6 : 1.5; controls.minPolarAngle = near ? 0.25 : 0.85; }
      const want = !s.locked ? 2.6 : 0; // dark inside until unlocked
      cabin.current.intensity += (want - cabin.current.intensity) * Math.min(1, dt * 5);
      // dim the interior's albedo while locked (scales every light source), restore on unlock
      const lvl = !s.locked ? 1 : 0.12; const k = Math.min(1, dt * 4);
      cabinLevel.current += (lvl - cabinLevel.current) * k;
      for (const m of rig.interiorMats) { m.color.copy(m.userData.base as THREE.Color).multiplyScalar(cabinLevel.current); m.envMapIntensity = 0.05 + 0.7 * (cabinLevel.current - 0.12) / 0.88; }
    }
    if (group.current) {
      const t = st.clock.elapsedTime;
      group.current.position.y = s.engineRunning ? Math.sin(t * 27) * 0.0012 + Math.sin(t * 3.1) * 0.0008 : 0;
    }
  });

  return (
    <group ref={group}>
      <primitive object={rig.wrap} />
      <Dash s={s} anchor={rig.dashAnchor} level={cabinLevel} />
      {/* indicator bloom while flashing */}
      {rig.indPos.map((p, i) => (
        <sprite key={i} position={p} scale={[0.34, 0.34, 1]} visible={ind || DEV_QS.has("flash")} renderOrder={10}>
          <spriteMaterial map={getGlowTexture()} transparent depthWrite={false} depthTest={false} blending={THREE.AdditiveBlending} opacity={0.95} />
        </sprite>
      ))}
      {/* warm dome lamp under the roof, centre of the cabin */}
      <pointLight ref={cabin} position={[0, 1.05, -0.25]} color="#ffd6a0" intensity={0} distance={3.2} decay={2} />
    </group>
  );
}

function StudioEnv() {
  const tex = useLoader(THREE.TextureLoader, ENV_URL);
  tex.mapping = THREE.EquirectangularReflectionMapping; tex.colorSpace = THREE.SRGBColorSpace;
  return <Environment map={tex} />;
}

function Rig({ children, autoRotate }: { children: ReactNode; autoRotate: boolean }) {
  return (
    <>
      <StudioEnv />
      <hemisphereLight args={["#cfe3ff", "#0a0f1a", 0.35]} />
      <directionalLight position={[4, 6, 3]} intensity={1.1} color="#ffffff" />
      <directionalLight position={[-5, 3, -4]} intensity={0.4} color="#9fd8ff" />
      {children}
      <ContactShadows position={[0, 0.005, 0]} opacity={0.65} scale={9} blur={2.2} far={1.6} color="#000" />
      {/* free-flow zoom: wheel / pinch carries the camera from the orbit view through the glass into the cabin */}
      <OrbitControls target={[0, 0.72, 0]} autoRotate={autoRotate} autoRotateSpeed={0.7} enablePan={false}
        minDistance={0.05} maxDistance={12} minPolarAngle={0.85} maxPolarAngle={1.5}
        zoomToCursor zoomSpeed={0.9} enableDamping dampingFactor={0.08} makeDefault />
    </>
  );
}

class Boundary extends Component<{ fallback: (err: string) => ReactNode; children: ReactNode }, { err: string | null }> {
  state = { err: null as string | null };
  static getDerivedStateFromError(e: Error) { return { err: e.message || "3D failed" }; }
  render() { return this.state.err ? this.props.fallback(this.state.err) : this.props.children; }
}

function webglOk(): boolean {
  try { const c = document.createElement("canvas"); return !!(c.getContext("webgl2") || c.getContext("webgl")); } catch { return false; }
}
const DEV_QS = new URLSearchParams(location.search);

export default function CarScene({ s, paint }: { s: CarState; paint: Paint }) {
  const [err, setErr] = useState<string | null>(webglOk() ? null : "no WebGL in this browser");
  const glow = s.killArmed ? "rgba(251,113,133,.55)" : s.locked ? "rgba(34,211,238,.5)" : "rgba(245,158,11,.5)";
  const fallback = (why: string) => (
    <div className="hero-svg"><CarGraphic s={s} paint={paint} /><span className="hero-err">3D unavailable — {why}</span></div>
  );
  if (err) return fallback(err);
  return (
    <div className="hero-3d" style={{ ["--glow" as string]: glow }}>
      <div className="hero-floor" />
      <Boundary fallback={(e) => { queueMicrotask(() => setErr(e)); return fallback(e); }}>
        <Suspense fallback={<div className="hero-loading"><span /> Loading Aktiar’s Dzire…</div>}>
          <Canvas dpr={[1, 2]} onCreated={({ camera }) => { const z = Number(DEV_QS.get("zoom")); if (z) { camera.position.set(0, 0.72, 0).add(new THREE.Vector3(-0.35, 0.25, 1).normalize().multiplyScalar(z)); } }} camera={{ fov: 26, position: [-5.3, 2.4, 6.4], near: 0.03, far: 60 }}
            gl={{ antialias: true, alpha: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.15 }}
            style={{ background: "transparent" }}>
            <Rig autoRotate={!DEV_QS.has("static")}>
              <CarModel s={s} paint={paint} />
            </Rig>
          </Canvas>
        </Suspense>
      </Boundary>
      <div className="hero-badges">
        {s.engineRunning && <span className="hb hb-on">ENGINE</span>}
        {s.ac && <span className="hb hb-ac">A/C {s.blower}</span>}
        {s.bootOpen && <span className="hb hb-warn">BOOT OPEN</span>}
        {(Object.entries(s.windows) as [WindowId, number][]).filter(([, v]) => v > 5).map(([k, v]) => (
          <span key={k} className="hb hb-warn">{k.toUpperCase()} {v >= 90 ? "DOWN" : "VENT"}</span>
        ))}
      </div>
    </div>
  );
}

useGLTF.preload(MODEL_URL, DRACO_PATH);
